/**
 * paperworkPresentation.js — what the seller is told about a paperwork result.
 *
 * Composes legal's verbatim copy (paperworkCopy.js) with the per-state
 * paperwork registry the server uses (backend/app/services/paperwork/
 * registry.py: labels, offer-form labels, disclosure forms, required uploads).
 * Customer-facing labels never name a WB form (legal copy §1.5).
 */
import {
  CATEGORY_CONDO,
  CATEGORY_RESIDENTIAL,
  CATEGORY_VACANT_LAND,
  DISCLOSURE_BASIS_VOLUNTARY,
  PROPERTY_TYPE_MOBILE_HOME,
  PROPERTY_TYPE_MULTI_FAMILY,
  STATUS_OK,
  normalizeUnitCount,
} from './paperworkCategory';
import { VOLUNTARY_TITLE_SUFFIX, copyText, hasPaperworkCopy, stateCopy } from './paperworkCopy';
import { DISCLOSURE_ACKNOWLEDGMENT_LABEL } from '../../utils/documentTypes';

/** Interim § 709.02(2) condominium addendum upload (registry required_uploads). */
export const UPLOAD_WI_CONDO_RECR_ADDENDUM = 'wi_condo_recr_addendum';

/**
 * A hard block outside a paperwork state: there is no reviewed state copy, so
 * say so plainly without naming any state's law. Legal-reviewed (copy file
 * §10, "Frontend-authored strings"; review §9.4), with the state-neutral
 * attorney-referral sentence appended in L4.
 */
export const GENERIC_BLOCK_MESSAGE =
  "cutthe6.com can't prepare paperwork for this property type. For help with this sale, consider talking with a real estate attorney licensed in your state.";

export const DISCLOSURE_TITLE_RECR = 'Real Estate Condition Report';
export const DISCLOSURE_TITLE_VLDR = 'Vacant Land Disclosure Report';

/**
 * The report is out for the seller's DocuSign signature and doesn't count
 * yet. Frontend-authored (not in legal's copy file): it states a fact about
 * the signing, not the law.
 */
export const disclosureSigningText = (title) =>
  `Your ${title || DISCLOSURE_TITLE_RECR} is waiting for your signature in DocuSign. Finish signing it before publishing.`;

/** Wisconsin rows of the server's paperwork registry, customer-facing fields only. */
export const WI_PAPERWORK_SPECS = {
  [CATEGORY_RESIDENTIAL]: {
    label: 'Wisconsin Residential',
    offerForm: copyText('WI.paperwork.offer_form.residential'),
    disclosureForm: 'recr',
    disclosureTitle: DISCLOSURE_TITLE_RECR,
    requiredUploads: [],
  },
  [CATEGORY_CONDO]: {
    label: 'Wisconsin Condominium',
    offerForm: copyText('WI.paperwork.offer_form.condo'),
    disclosureForm: 'recr',
    disclosureTitle: DISCLOSURE_TITLE_RECR,
    requiredUploads: [UPLOAD_WI_CONDO_RECR_ADDENDUM],
  },
  [CATEGORY_VACANT_LAND]: {
    label: 'Wisconsin Vacant Land',
    offerForm: copyText('WI.paperwork.offer_form.vacant_land'),
    disclosureForm: 'vldr',
    disclosureTitle: DISCLOSURE_TITLE_VLDR,
    requiredUploads: [],
  },
};

/** Seller/buyer-facing category names for places without a state label. */
export const CATEGORY_LABELS = {
  [CATEGORY_RESIDENTIAL]: 'Residential',
  [CATEGORY_CONDO]: 'Condominium',
  [CATEGORY_VACANT_LAND]: 'Vacant land',
};

const normState = (stateCode) => String(stateCode || '').trim().toUpperCase();

/** The registry spec for a state and ok result, or null. */
export function specFor(stateCode, result) {
  if (normState(stateCode) !== 'WI' || result?.status !== STATUS_OK) return null;
  return WI_PAPERWORK_SPECS[result.category] || null;
}

/** "Vacant Land Disclosure Report (Voluntary)", or null outside the registry. */
export function disclosureTitleFor(stateCode, result) {
  const spec = specFor(stateCode, result);
  if (!spec) return null;
  return result.disclosureBasis === DISCLOSURE_BASIS_VOLUNTARY
    ? `${spec.disclosureTitle}${VOLUNTARY_TITLE_SUFFIX}`
    : spec.disclosureTitle;
}

/**
 * "Buyer's Acknowledgment of Receipt — Real Estate Condition Report" (or
 * "— Vacant Land Disclosure Report"): the buyer-signed document the server
 * adds to the post-acceptance envelope. Matches the server's title, which
 * names the form without "(Voluntary)". Null outside the registry.
 */
export function disclosureAcknowledgmentTitle(stateCode, result) {
  const spec = specFor(stateCode, result);
  return spec ? `${DISCLOSURE_ACKNOWLEDGMENT_LABEL} — ${spec.disclosureTitle}` : null;
}

/** WI.paperwork.line for an ok result, e.g. "Paperwork: Wisconsin Condominium. ..." */
export function paperworkLine(stateCode, result) {
  const spec = specFor(stateCode, result);
  if (!spec) return null;
  return copyText('WI.paperwork.line', {
    label: spec.label,
    offer_form: spec.offerForm,
    title: disclosureTitleFor(stateCode, result),
  });
}

/** WI.paperwork.reason.* — why this paperwork, in the seller's own answers. */
export function paperworkReason(stateCode, result, profile) {
  if (!specFor(stateCode, result)) return null;
  if (result.category === CATEGORY_CONDO) return copyText('WI.paperwork.reason.condo');
  if (result.category === CATEGORY_VACANT_LAND) {
    return copyText(`WI.paperwork.reason.vacant_land.${result.disclosureBasis}`);
  }
  if (result.propertyType === PROPERTY_TYPE_MULTI_FAMILY) {
    return copyText('WI.paperwork.reason.residential.multi_family', {
      unit_count: normalizeUnitCount(profile?.unit_count),
    });
  }
  if (result.propertyType === PROPERTY_TYPE_MOBILE_HOME) {
    return copyText('WI.paperwork.reason.residential.mobile_home');
  }
  return copyText('WI.paperwork.reason.residential.home');
}

/**
 * The message for a non-ok result in the state's copy, with the attorney line
 * when the result carries a referral and the message doesn't already end with
 * it (legal §0: never append it twice). Null when the state has no copy.
 */
export function resultMessage(stateCode, result) {
  if (!result || result.status === STATUS_OK) return null;
  const message = stateCopy(stateCode, result.messageKey);
  if (!message) return null;
  const attorney = stateCopy(stateCode, 'paperwork.attorney_referral');
  if (result.attorneyReferral && attorney && !message.includes(attorney)) {
    return `${message} ${attorney}`;
  }
  return message;
}

/** Uploads the seller must add before publishing (e.g. the condo addendum). */
export function requiredUploadsFor(stateCode, result) {
  return specFor(stateCode, result)?.requiredUploads || [];
}

/** True when the state routes paperwork (asks the questions, gates publish). */
export const isPaperworkState = (stateCode) => hasPaperworkCopy(stateCode);
